import {
  COMMERCIAL_USE_TERMS,
  EXPORT_LONG_EDGE,
  EXPORT_QUALITY_LABELS,
  PLANS,
} from '@shared/plans';
import type { ApiKeyStore } from '@/capture/ApiKeyStore';
import { looksLikeApiKey } from '@/capture/ApiKeyStore';
import type { License } from '@/cloud/License';
import { clear, el, setClass } from './dom';
import { icon } from './Icons';

/**
 * **License and API key** — the panel directly under Localize Site.
 *
 * ## Two facts, one place
 *
 * What am I entitled to, and can this app actually render? They are separate answers — the badge
 * comes from the account, the key from wherever the credential lives (see
 * {@link licenseFor} for why a browser-held key deliberately does not move the badge) — and until
 * now they were in two different dialogs, neither of which anybody opens until something has already
 * gone wrong. A user could sit in front of a fully working editor for an hour with no key stored,
 * and find out at the moment they pressed Enhance.
 *
 * So both are stated up front, in the same column as the work, in the same `lg-panel` chrome as
 * Localize Site above it and the flora palette below.
 *
 * ## The missing key flashes, and nothing else does
 *
 * A pulsing element in a design tool is a real cost — it is in peripheral vision the whole time
 * someone is placing plants — so exactly one state earns it. A missing key is the one state where
 * the app is quietly unable to do the thing it is for: every AI path (Enhance, the wand, swatches,
 * site plans) fails at the point of use, and there is no other symptom until then. A stored key, a
 * managed key, and every licence tier are all reported flat.
 *
 * The pulse is opacity on a chip that is already red and legible at its dimmest, so under
 * `prefers-reduced-motion` it simply stops animating and loses nothing (see `hud.css`).
 *
 * ## And the fix is in the panel, not behind it
 *
 * When there is no key the body opens itself onto the field that fixes it. Sending someone to a
 * dialog to resolve an alert raised here would make the alert a signpost rather than a control, and
 * this is a two-second job: paste, save, done. The one case that *is* handed off is a managed
 * credential — the server holds it, the browser must never see it, and Account is genuinely where
 * it is changed.
 */

export interface LicensePanelDeps {
  /** The credential store — read per refresh, and subscribed to so the row cannot go stale. */
  apiKeys: ApiKeyStore;
  /** What the account is entitled to. Re-read on every refresh, so signing in redraws the badge. */
  license: () => License;
  /** Follows the flora palette: a 44px rail has no room for a badge. */
  collapsed?: () => boolean;
  /** Open the account dialog — where a *managed* key is changed. Omitted on a local-only build. */
  onManageAccount?: () => void;
}

export class LicensePanel {
  readonly root: HTMLElement;

  private readonly badge: HTMLElement;
  /** The flashing chip in the head — no key, or no credits. See the class comment. */
  private readonly alert: HTMLElement;
  /** The chip's wording, swapped rather than rebuilt so the icon beside it never reflows. */
  private readonly alertLabel: HTMLElement;
  private readonly chevron: HTMLButtonElement;
  private readonly body: HTMLElement;
  private readonly detail: HTMLElement;
  /** What the plan grants beyond credits — the three limits phase 6 actually enforces. */
  private readonly planSection: HTMLElement;
  private readonly keySection: HTMLElement;
  private readonly message: HTMLElement;
  private shut: boolean;
  private readonly unsubscribe: () => void;

  constructor(private readonly deps: LicensePanelDeps) {
    this.badge = el('span', { class: 'lg-panel__count lg-license__badge' });
    this.alertLabel = el('span', { text: 'No API key' });
    this.alert = el(
      'span',
      {
        class: 'lg-license__alert',
        title: 'No OpenAI API key, so nothing in this app can render',
        attrs: { role: 'status' },
      },
      el('span', { class: 'lg-license__alerticon', html: icon('warning', 12) }),
      this.alertLabel,
    );
    this.chevron = el('button', {
      class: 'lg-panel__collapse',
      html: icon('chevron', 16),
      attrs: { type: 'button' },
      on: { click: () => this.toggle() },
    });

    this.detail = el('p', { class: 'lg-legend lg-license__detail' });
    this.planSection = el('div', { class: 'lg-license__plan' });
    this.keySection = el('div', { class: 'lg-license__key' });
    this.message = el('p', { class: 'lg-legend lg-license__message', attrs: { hidden: true } });

    this.body = el(
      'div',
      { class: 'lg-panel__body' },
      el(
        'div',
        { class: 'lg-license' },
        this.detail,
        this.planSection,
        el('h3', { class: 'lg-section__title', text: 'OpenAI API key' }),
        this.keySection,
        this.message,
      ),
    );

    this.root = el(
      'aside',
      { class: 'lg-panel lg-panel--license', attrs: { 'aria-label': 'License and API key' } },
      el(
        'div',
        { class: 'lg-panel__head' },
        el('span', { class: 'lg-panel__icon lg-license__mark', html: icon('star', 16) }),
        el('h2', { class: 'lg-panel__title', text: 'License' }),
        this.alert,
        this.badge,
        this.chevron,
      ),
      this.body,
    );

    // Opened only by the state that needs an answer typed into it. A key that is already there is a
    // fact the head reports on its own, and the column belongs to the plants.
    this.shut = deps.apiKeys.has();
    // Saving a key from the Enhance panel, or signing in and picking up a managed one, has to land
    // here too — otherwise this panel becomes the one place in the app still claiming there is none.
    this.unsubscribe = deps.apiKeys.onChange(() => this.refresh());
    this.refresh();
  }

  /** Redraw from the session and the key store — after signing in, and after a key is saved. */
  refresh(): void {
    const license = this.deps.license();
    const missing = !this.deps.apiKeys.has();

    this.badge.textContent = license.label;
    this.badge.title = license.detail;
    // On the badge and on the root: the badge takes the metal, and the root lets the head's mark
    // take it too without a `:has()` that would be doing selector work for a one-line state.
    for (const tier of ['bronze', 'silver', 'gold'] as const) {
      const on = license.tier === tier;
      setClass(this.badge, `is-${tier}`, on);
      setClass(this.root, `is-${tier}`, on);
    }
    this.detail.textContent = license.detail;

    // The two alert states are exclusive by construction, not just by this branch: a managed
    // credential makes `has()` true, so an account drawing on the operator's allowance can never
    // read as keyless. Where they could somehow overlap, no key wins — it is the more total
    // failure, since a spent allowance still leaves an account that renders again next month.
    const dry = !missing && license.creditsLeft === 0;
    this.alert.hidden = !missing && !dry;
    if (missing) {
      this.alertLabel.textContent = 'No API key';
      this.alert.title = 'No OpenAI API key, so nothing in this app can render';
    } else if (dry) {
      this.alertLabel.textContent = 'No credits';
      this.alert.title =
        'This month’s AI credits are spent. Renders resume when the allowance resets, or ' +
        'immediately on your own OpenAI key.';
    }
    setClass(this.root, 'is-keyless', missing);
    setClass(this.root, 'is-dry', dry);

    this.renderPlan(license.plan);
    this.renderKey();
    this.applyShut();
    setClass(this.root, 'is-collapsed', this.deps.collapsed?.() ?? false);
  }

  /**
   * What this plan grants, beyond the credit figure the detail line already gives.
   *
   * Three rows, and each is a limit the app really applies — a fourth that was merely aspirational
   * would poison the other three. Anonymous gets nothing here: there is no plan, and inventing a
   * row that said "Personal use only" about the standalone tool would be claiming a restriction
   * the app does not impose.
   *
   * The commercial line is the one that cannot be enforced, and it is stated for exactly that
   * reason — an unenforceable term that is never shown is not a term at all. See
   * `COMMERCIAL_USE_TERMS`.
   */
  private renderPlan(id: License['plan']): void {
    clear(this.planSection);
    if (id === null) return;

    const plan = PLANS[id];
    const ceiling = EXPORT_LONG_EDGE[plan.exportQuality];
    this.planSection.appendChild(
      el(
        'dl',
        { class: 'lg-license__grants' },
        ...grant('Saved sites', plan.projectLimit === null ? 'Unlimited' : String(plan.projectLimit)),
        ...grant(
          'Export size',
          `${EXPORT_QUALITY_LABELS[plan.exportQuality]} · up to ${String(ceiling)}px`,
        ),
        ...grant('Commercial use', COMMERCIAL_USE_TERMS[plan.commercialUse]),
      ),
    );
  }

  /**
   * The key row, in its three states.
   *
   * Rebuilt rather than updated in place: the three states share no controls, and a row that swaps a
   * password field for a Forget button by toggling `hidden` on both is the kind of thing that ends up
   * showing a field the user can type a secret into while the app is not listening.
   */
  private renderKey(): void {
    clear(this.keySection);
    const keys = this.deps.apiKeys;

    if (keys.isManaged) {
      this.keySection.appendChild(
        el('p', {
          class: 'lg-legend lg-license__keytext',
          text: `Held for you by the server — ${keys.masked() ?? 'managed by your account'}. It is never sent to this browser.`,
        }),
      );
      if (this.deps.onManageAccount) {
        this.keySection.appendChild(
          el('button', {
            class: 'lg-button',
            text: 'Manage in Account',
            attrs: { type: 'button' },
            on: { click: () => this.deps.onManageAccount?.() },
          }),
        );
      }
      return;
    }

    if (keys.has()) {
      this.keySection.appendChild(
        el(
          'p',
          { class: 'lg-legend lg-license__keytext' },
          el('span', { class: 'lg-license__keyicon', html: icon('key', 12) }),
          el('span', { text: `Stored on this machine · ${keys.masked() ?? ''}` }),
        ),
      );
      this.keySection.appendChild(
        el('button', {
          class: 'lg-button',
          text: 'Forget key',
          title: 'Remove the key from this browser. AI rendering stops until another is added.',
          attrs: { type: 'button' },
          on: {
            click: () => {
              keys.clear();
              this.setMessage('Key cleared. Nothing in this app can render until one is added.');
            },
          },
        }),
      );
      return;
    }

    const field = el('input', {
      class: 'lg-input lg-license__field',
      attrs: {
        type: 'password',
        placeholder: 'sk-…',
        'aria-label': 'OpenAI API key',
        spellcheck: 'false',
        autocomplete: 'off',
      },
      on: {
        keydown: (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.saveKey(field.value);
        },
        input: () => this.setMessage(''),
      },
    });

    this.keySection.appendChild(
      el(
        'div',
        { class: 'lg-license__row' },
        field,
        el('button', {
          class: 'lg-button lg-button--primary',
          text: 'Save',
          attrs: { type: 'button' },
          on: { click: () => this.saveKey(field.value) },
        }),
      ),
    );
    this.keySection.appendChild(
      el('p', {
        class: 'lg-legend',
        text:
          'Kept in this browser and sent straight to OpenAI. Renders, the wand, swatches and site ' +
          'plans all need it.',
      }),
    );
  }

  /**
   * Store a pasted key.
   *
   * The shape check is worth the two lines: the usual failure is a key pasted with a word of
   * surrounding text or a truncated copy, and finding that out here costs nothing while finding it
   * out at the API costs a round trip and an error that reads like the app is broken.
   */
  private saveKey(value: string): void {
    const trimmed = value.trim();
    if (trimmed === '') {
      this.setMessage('Paste a key first — they start with “sk-”.');
      return;
    }
    if (!looksLikeApiKey(trimmed)) {
      this.setMessage('That does not look like an OpenAI key — they start with “sk-”.');
      return;
    }
    this.setMessage('');
    // Emits, which refreshes this panel through the subscription above.
    this.deps.apiKeys.set(trimmed);
  }

  private setMessage(text: string): void {
    this.message.textContent = text;
    this.message.hidden = text === '';
  }

  private toggle(): void {
    this.shut = !this.shut;
    this.applyShut();
  }

  private applyShut(): void {
    setClass(this.root, 'is-shut', this.shut);
    this.chevron.setAttribute('aria-expanded', String(!this.shut));
    this.chevron.title = this.shut ? 'Show license and API key' : 'Hide license and API key';
    this.chevron.setAttribute('aria-label', this.chevron.title);
  }

  dispose(): void {
    this.unsubscribe();
    this.root.remove();
  }
}

/** One term/definition pair. Returned as a pair so the caller spreads it into a single `dl`. */
function grant(label: string, value: string): [HTMLElement, HTMLElement] {
  return [
    el('dt', { class: 'lg-license__grantkey', text: label }),
    el('dd', { class: 'lg-license__grantval', text: value }),
  ];
}
